// CampaignSetup.tsx (updated - removed scheduling logic)
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  FaEnvelope,
  FaRegClock,
  FaSms,
  FaTelegramPlane,
  FaVoicemail,
  FaWeebly,
  FaWhatsapp,
} from "react-icons/fa";
import { X, Users, Search, PenLine } from "lucide-react";
import { toast } from "sonner";
import UploadTab from "./upload-tab";
import ManualTab from "./manual-tab";
import { CampaignData } from "../../../types";
import { SingleAudienceResponse, SingleCampaignResponse } from "../../../../pages/campaign/types";
import useRequest from "../../../hook/use-req";
import ConvoAIPanel, { ConvoAIMessage } from "../convoai-hub";
import Input from "../../../input";
import Select from "../../../select";
import { ApiAudience, Audience, SenderData } from "../../../../pages/create-campaign/types";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CampaignSetupProps {
  campaignData: CampaignData;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
  audienceById: SingleAudienceResponse | null;
}

const CHANNEL_OPTIONS = [
  { value: "sms", label: "SMS", icon: FaSms },
  { value: "whatsapp", label: "WhatsApp", icon: FaWhatsapp },
  { value: "email", label: "Email", icon: FaEnvelope },
  { value: "rcs", label: "RCS", icon: FaRegClock },
  { value: "voice", label: "Voice", icon: FaVoicemail },
  { value: "webchat", label: "Web Chat", icon: FaWeebly },
  { value: "telegram", label: "Telegram", icon: FaTelegramPlane },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const CampaignSetup: React.FC<CampaignSetupProps> = ({
  campaignData,
  singleCampaign,
  setCampaignData,
  onSubmit,
  onValidation,
  audienceById,
}) => {
  const token = localStorage.getItem("authToken");

  // ── State ──
  const [senders, setSenders] = useState<SenderData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brief, setBrief] = useState("");

  // Target audience
  const [audienceTab, setAudienceTab] = useState<"segments" | "upload" | "manual">("segments");
  const [audienceSearch, setAudienceSearch] = useState("");
  const [allAudiences, setAllAudiences] = useState<Audience[]>([]);
  const selectedAudiences = campaignData.selectedAudiences || [];

  // Upload tab state
  const [uploadAudienceId, setUploadAudienceId] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedContacts, setUploadedContacts] = useState<number>(0);

  // Manual entry state
  const [manualAudienceId, setManualAudienceId] = useState<string | null>(null);
  const [manualValidRecords, setManualValidRecords] = useState<number>(0);

  // Broadcast tags
  const [tagInput, setTagInput] = useState("");
  const tags = campaignData.tags || [];

  // ConvoAI
  const [isConvoOpen, setIsConvoOpen] = useState(true);

  // ── Requests ──
  const { makeRequest } = useRequest("/campaign", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: editCampaign } = useRequest(
    `/campaign/${singleCampaign?.campaign_id}/edit`,
    "PUT",
    { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: senderNames } = useRequest("/senders/all", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: fetchAudiences } = useRequest("/audience/list", "GET", {
    Authorization: `Bearer ${token}`,
  });

  // ── Form ──
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      campaign_name: campaignData.campaign_name || "",
      campaign_goal: campaignData.campaign_goal || "",
      campaign_channel: campaignData.campaign_channel || "",
      sender_name: campaignData.sender_name || "",
    },
  });

  const channel = watch("campaign_channel");
  const isEmailCampaign = channel === "email";

  // ── Effects ──
  useEffect(() => {
    const fetchData = async () => {
      const [res] = await senderNames();
      if (res && Array.isArray(res)) setSenders(res);
      else if (res?.data && Array.isArray(res.data)) setSenders(res.data);
    };
    fetchData();
  }, []);

  // Fetch audiences from API
  useEffect(() => {
    const fetchAudiencesData = async () => {
      try {
        const [response] = await fetchAudiences();
        if (response && response.audiences && Array.isArray(response.audiences)) {
          const sortedAudiences = response.audiences.sort(
            (a: ApiAudience, b: ApiAudience) => {
              return (
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
              );
            }
          );

          const transformedAudiences: Audience[] = sortedAudiences.map((audience: ApiAudience) => ({
            id: audience.audience_id,
            audience_id: audience.audience_id,
            name: audience.metadata.unique_file_name.replace(".csv", ""),
            contacts: audience.metadata.unique_count,
            uploadDate: new Date(audience.created_at).toLocaleDateString(),
            status: audience.status,
          }));

          setAllAudiences(transformedAudiences);
        }
      } catch (error) {
        console.error("Error fetching audiences:", error);
      }
    };

    fetchAudiencesData();
  }, []);

  useEffect(() => {
    if (singleCampaign) {
      reset({
        campaign_name: singleCampaign.campaign_name || "",
        campaign_channel: singleCampaign.campaign_channel?.toLowerCase() || "",
        sender_name: singleCampaign.sender_name || "",
        campaign_goal: singleCampaign?.campaign_goal || "",
      });


      if (singleCampaign.broadcast_tags) {
        setCampaignData(prev => ({
          ...prev,
          tags: singleCampaign.broadcast_tags.map((tag: string) =>
            tag.startsWith("#") ? tag : `#${tag}`
          ),
        }));
      }
    }
  }, [singleCampaign, reset]);

  useEffect(() => {
    const ids = getSelectedAudienceIds();

    if (ids.length > 0) {
      localStorage.setItem("audienceId", ids[0]); // or store all if needed
    } else {
      localStorage.removeItem("audienceId");
    }
  }, [selectedAudiences, uploadAudienceId, manualAudienceId]);

  useEffect(() => {
    onValidation(isValid);
  }, [isValid, onValidation]);

  // ── Helpers ──
  const getSenderOptions = () => {
    return senders
      .map((s) => {
        let value = "";
        switch (channel) {
          case "sms": value = s.sender_id?.sms; break;
          case "whatsapp": value = s.sender_id.whatsapp; break;
          case "email": value = s.sender_id.email; break;
          case "voice": value = s.sender_id.phone; break;
          default: value = s.sender_id.sms;
        }
        return value ? { value, label: value } : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  };

  const getSenderLabel = () => {
    switch (channel) {
      case "sms": return "SMS Sender ID";
      case "whatsapp": return "Sender Profile";
      case "email": return "Email Address";
      case "voice": return "Phone Number";
      default: return "Sender Profile";
    }
  };

  const totalApproxReach =
    selectedAudiences.reduce((acc, s) => acc + s.contacts, 0) +
    (uploadedContacts || 0) +
    (manualValidRecords || 0);

  // Get selected audience IDs for the API payload
  const getSelectedAudienceIds = (): string[] => {
    const ids = [
      ...selectedAudiences.map(a => a.audience_id),
      uploadAudienceId,
      manualAudienceId,
    ].filter(Boolean) as string[];

    return Array.from(new Set(ids));
  };

  // ── Audience handlers ──
  const toggleAudience = (audience: Audience) => {
    setCampaignData(prev => {
      const exists = prev.selectedAudiences?.find(a => a.id === audience.id);

      return {
        ...prev,
        selectedAudiences: exists
          ? (prev.selectedAudiences || []).filter(a => a.id !== audience.id)
          : [...(prev.selectedAudiences || []), audience],
      };
    });
  };

  const removeAudience = (id: string) => {
    setCampaignData((prev) => ({
      ...prev,
      selectedAudiences: (prev.selectedAudiences || []).filter((a) => a.id !== id),
    }));

    // Clear upload/manual if needed
    if (uploadAudienceId === id) {
      setUploadAudienceId(null);
      setUploadedFileName(null);
      setUploadedContacts(0);
    }

    if (manualAudienceId === id) {
      setManualAudienceId(null);
      setManualValidRecords(0);
    }
  };

  const filteredAudiences = allAudiences.filter((a) =>
    a.name.toLowerCase().includes(audienceSearch.toLowerCase())
  );

  // ── Upload handlers ──
  const handleUploadSuccess = (audienceId: string, fileName: string, contacts: number) => {
    setUploadAudienceId(audienceId);
    setUploadedFileName(fileName);
    setUploadedContacts(contacts);

    setCampaignData(prev => ({
      ...prev,
      selectedAudiences: [
        ...(prev.selectedAudiences || []),
        {
          id: audienceId,
          audience_id: audienceId,
          name: fileName.replace(/\.(csv|xlsx)$/i, ""),
          contacts,
          uploadDate: new Date().toLocaleDateString(),
          status: "completed",
        }
      ]
    }));
  };

  // ── Manual handlers ──
  const handleManualSuccess = (audienceId: string, validRecords: number) => {
    setManualAudienceId(audienceId);
    setManualValidRecords(validRecords);

    setCampaignData(prev => {
      const exists = (prev.selectedAudiences || []).find(a => a.id === audienceId);
      if (exists) return prev;

      return {
        ...prev,
        selectedAudiences: [
          ...(prev.selectedAudiences || []),
          {
            id: audienceId,
            audience_id: audienceId,
            name: `Manual Entry`,
            contacts: validRecords,
            uploadDate: new Date().toLocaleDateString(),
            status: "completed",
          }
        ]
      };
    });
  };

  // ── Tag handlers ──
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === " ") && tagInput.trim()) {
      e.preventDefault();

      const tag = tagInput.startsWith("#") ? tagInput : `#${tagInput}`;

      setCampaignData((prev: CampaignData) => {
        const existingTags = prev.tags || [];

        if (existingTags.includes(tag)) return prev;

        return {
          ...prev,
          tags: [...existingTags, tag],
        };
      });

      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setCampaignData((prev: CampaignData) => ({
      ...prev,
      tags: (prev.tags || []).filter((t) => t !== tag),
    }));
  };

  // ── Submit ──
  const buildFormattedData = (data: any) => {
    return {
      campaign_name: data.campaign_name,
      campaign_goal: data.campaign_goal,
      campaign_channel: data.campaign_channel,
      sender_name: data.sender_name,
      audience_ids: getSelectedAudienceIds(),
      broadcast_tags: data?.broadcast_tags || tags.map(tag => tag.replace('#', '')),
      // No scheduling fields here - they will be added in ScheduleLaunch
    };
  };

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);

    const formattedData = buildFormattedData(data);
    console.log("Submitting campaign data:", formattedData);

    const [response, status] = await makeRequest(formattedData);
    if (status === 201) {
      toast.success("Campaign setup created successfully!");
      const campaignId = response?.response?.campaign_id;
      if (campaignId) localStorage.setItem("campaignId", String(campaignId));

      // Call onSubmit and it will automatically advance to next step
      onSubmit({
        ...formattedData,
        id: campaignId,
      });
    } else {

      toast.error(
        typeof response?.detail === "string"
          ? response.detail
          : JSON.stringify(response?.detail || '"Failed to create campaign"')
      );
      setIsSubmitting(false);
    }

  };

  const handleEditFormSubmit = async (data: any) => {
    setIsSubmitting(true);

    const formattedData = buildFormattedData(data);
    console.log("Editing campaign data:", formattedData);

    const [response, status] = await editCampaign({
      ...formattedData,
      channel: formattedData.campaign_channel
    });

    if (status === 200) {
      toast.success("Campaign updated successfully!");
      const campaignId = response?.response?.campaign_id;
      if (campaignId) localStorage.setItem("campaignId", String(campaignId));

      onSubmit({
        ...formattedData,
        id: campaignId,
      });
    } else {
      toast.error(response?.detail || "Failed to update campaign");
      setIsSubmitting(false);
    }

  };

  // ── Convo AI ──
  const handleConvoSend = async (
    message: string,
    _history: ConvoAIMessage[]
  ): Promise<string> => {
    await new Promise((r) => setTimeout(r, 800));
    setBrief(`Campaign: ${message.slice(0, 120)}${message.length > 120 ? "..." : ""}`);
    return "Got it! I've pre-filled the broadcast brief. Tweak it and hit Generate when ready.";
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex my-10 gap-4 h-full">
      {/* ── Main Form ── */}
      <div className="flex-1 w-full">
        <form
          id="step-1-form"
          onSubmit={handleSubmit(singleCampaign ? handleEditFormSubmit : handleFormSubmit)}
          className="space-y-5"
        >
          {/* Header */}
          <div>
            <h2 className="text-xl font-bold text-gray-800">Broadcast Details</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              We have pre-filled this based on your brief. You may edit anything or proceed.
            </p>
          </div>

          {/* Row 1: Campaign Name + Campaign Goal */}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="campaign_name"
              control={control}
              rules={{ required: "Campaign name is required" }}
              render={({ field, fieldState }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Campaign Name</label>
                  <Input
                    type="text"
                    name="name"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val)}
                    error={fieldState?.error?.message}
                    autoComplete="off"
                  />
                </div>
              )}
            />
            <Controller
              name="campaign_goal"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Campaign Goal</label>
                  <Input
                    type="text"
                    name="goal"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val)}
                    autoComplete="off"
                  />
                </div>
              )}
            />
          </div>

          {/* Row 2: Channels + Sender Profile */}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="campaign_channel"
              control={control}
              rules={{ required: "Channel is required" }}
              render={({ field, fieldState }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Channels</label>
                  <Select
                    label=""
                    name="channel"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val)}
                    error={fieldState?.error?.message}
                    options={CHANNEL_OPTIONS}
                  />
                </div>
              )}
            />
            <Controller
              name="sender_name"
              control={control}
              rules={{ required: "Sender is required" }}
              render={({ field, fieldState }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">{getSenderLabel()}</label>
                  <Select
                    label=""
                    name="sender"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val)}
                    error={fieldState?.error?.message}
                    options={getSenderOptions()}
                  />
                </div>
              )}
            />
          </div>

          {/* Target Audience */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Target Audience</label>

            {/* Tab switcher — 3 columns */}
            <div className="grid grid-cols-3 rounded-lg overflow-hidden border border-gray-200 mb-3">
              <button
                type="button"
                onClick={() => setAudienceTab("segments")}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-r border-gray-200 ${audienceTab === "segments"
                  ? "bg-white text-gray-800"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
              >
                <Users size={15} />
                Saved Audiences
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab("upload")}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-r border-gray-200 ${audienceTab === "upload"
                  ? "bg-white text-gray-800"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
              >
                <Users size={15} />
                Upload List
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab("manual")}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${audienceTab === "manual"
                  ? "bg-white text-gray-800"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
              >
                <PenLine size={15} />
                Enter Manually
              </button>
            </div>

            {/* Selected audience chips */}
            {(selectedAudiences.length > 0 || uploadedFileName || manualValidRecords > 0) && (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {selectedAudiences.map((audience) => (
                  <span
                    key={audience.id}
                    className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full"
                  >
                    {audience.name}
                    <button type="button" onClick={() => removeAudience(audience.id)} className="hover:text-green-900">
                      <X size={11} />
                    </button>
                  </span>
                ))}
                {uploadedFileName && audienceTab !== "upload" && (
                  <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    {uploadedFileName} ({uploadedContacts} contacts)
                    <button type="button" onClick={() => uploadAudienceId && removeAudience(uploadAudienceId)} className="hover:text-blue-900">
                      <X size={11} />
                    </button>
                  </span>
                )}
                {manualValidRecords > 0 && audienceTab !== "manual" && (
                  <span className="inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 text-violet-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    {manualValidRecords} manual {isEmailCampaign ? "email" : "phone"}{manualValidRecords > 1 ? "s" : ""}
                    <button type="button" onClick={() => manualAudienceId && removeAudience(manualAudienceId)} className="hover:text-violet-900">
                      <X size={11} />
                    </button>
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-500 font-medium">
                  {totalApproxReach.toLocaleString()} Approx. Reach
                </span>
              </div>
            )}

            {/* ── Saved Audiences tab ── */}
            {audienceTab === "segments" && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search saved audiences by name..."
                    value={audienceSearch}
                    onChange={(e) => setAudienceSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border-b border-gray-200 outline-none focus:bg-gray-50 bg-white"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {filteredAudiences.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 py-6">No saved audiences found</p>
                  ) : (
                    filteredAudiences.map((audience) => {
                      const isChecked = !!(selectedAudiences || []).find((a) => a.id === audience.id);
                      return (
                        <label
                          key={audience.id}
                          className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${isChecked ? "bg-green-50/40" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleAudience(audience)}
                            className="mt-0.5 accent-green-600 w-4 h-4 rounded cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isChecked ? "text-green-700" : "text-gray-700"}`}>
                              📁 {audience.name}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              Uploaded: {audience.uploadDate} • Status: {audience.status}
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                            {audience.contacts?.toLocaleString()}<br />
                            <span className="text-gray-400">Contacts</span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                {(selectedAudiences || []).length > 0 && (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                    {selectedAudiences.length} audience{selectedAudiences.length > 1 ? "s" : ""} selected
                  </div>
                )}
              </div>
            )}

            {/* ── Upload tab ── */}
            {audienceTab === "upload" && (
              <UploadTab
                channel={channel}
                onUploadSuccess={handleUploadSuccess}
                audienceById={audienceById}
                // campaignData={campaignData}
                // setCampaignData={setCampaignData}
                onValidation={onValidation}
              />
            )}

            {/* ── Manual tab ── */}
            {audienceTab === "manual" && (
              <ManualTab
                channel={channel}
                isEmailCampaign={isEmailCampaign}
                onManualSuccess={handleManualSuccess}
                audienceById={audienceById}
                campaignData={campaignData}
                setCampaignData={setCampaignData}
                onValidation={onValidation}
              />
            )}
          </div>

          {/* Broadcast Tags */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Broadcast Tags</label>
            <div className="flex flex-wrap items-center gap-2 min-h-[44px] px-3 py-2 border border-gray-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-200 focus-within:border-blue-400 transition-all">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded px-2 py-0.5"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-gray-900">
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder={tags.length === 0 ? "Type a tag and press Enter..." : ""}
                className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-2">
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting || ((selectedAudiences || []).length === 0 && !uploadAudienceId && !manualAudienceId)}
              className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Saving..." : "Continue to Message"}
            </button>
          </div>
        </form>
      </div>

      {/* ── ConvoAI Side Panel ── */}
      <aside className="hidden md:flex w-80 shrink-0 border-l border-slate-200 flex-col">
        <ConvoAIPanel
          isOpen={isConvoOpen}
          onClose={() => setIsConvoOpen(false)}
          onSend={handleConvoSend}
        />

        {!isConvoOpen && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setIsConvoOpen(true)}
              className="flex items-center gap-2 text-violet-600 border border-violet-200 hover:bg-violet-50 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Open ConvoAI
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};